import React, { useContext, useMemo } from "react";

import JSZip from "jszip";

import { Dropdown, Icon, classnames } from "@culturehq/components";

import { Trans, errorService, getMediaFromStory, t } from "app/utils";
import imagesSizes from "constants/imagesSizes";
import { Preview } from "@creatomate/preview";
import { CreatomateContext } from "../../contexts/VideoStudioContext";
import { convertVTTtoSRT, isSubtitleText, updateSubtitlesText } from "../../sidebar/tabs/subtitle/utils";

interface DownloadDropdownProps {
  onDownload: () => void;
  preview: Preview;
}

const DownloadDropdown: React.FC<DownloadDropdownProps> = ({ onDownload, preview }) => {
  const { data: { story } } = useContext(CreatomateContext);
  const { ready } = preview;
  const { data } = useContext(CreatomateContext);
  const { loading, pendingSubtitle, uploadingMedia } = data;

  const { elements } = preview.state || { elements: [] };
  const allSubtitleElements = useMemo(() => elements.filter(
    element => isSubtitleText(element.source.name)
  ).map(element => element.source), [elements]);

  const downloadZIP = async () => {
    try {
      const zip = new JSZip();

      // just when it is a montage
      if (!story) return;

      const updatedSubtitles = updateSubtitlesText(allSubtitleElements);

      // Fetch the video content
      const videoUrl = getMediaFromStory(imagesSizes.SHAREABLE, story);
      const videoFilename = "video.mp4";
      const videoResponse = await fetch(videoUrl);
      if (!videoResponse.ok) throw new Error(`Failed to fetch video: ${videoResponse.statusText}`);
      const videoContent = await videoResponse.blob();
      zip.file(videoFilename, videoContent);

      updatedSubtitles.forEach(subtitle => {
        const vttContent = subtitle.text;
        const subtitleSRTContent = convertVTTtoSRT(vttContent);
        zip.file(`${subtitle.language}.vtt`, new Blob([vttContent], { type: "text/vtt" }));
        zip.file(`${subtitle.language}.srt`, new Blob([subtitleSRTContent], { type: "text/srt" }));
      });

      // Generate the zip file and trigger download
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "video_and_captions.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      errorService.error(`Error creating zip file: ${error}`);
    }
  };

  const handleOptionSelect = (selectedValue: string) => {
    switch (selectedValue) {
      case "download_media":
        onDownload();
        break;
      case "download_with_captions":
        downloadZIP();
        break;
      default:
        break;
    }
  };

  const isButtonDisabled = !!loading || !ready || uploadingMedia || !!pendingSubtitle
    || elements.length === 0;

  return (
    <div className={`${isButtonDisabled ? "Download-Btn_disabled" : ""}`}>
      <Dropdown onChange={handleOptionSelect} selected="">
        <Dropdown.Button className={classnames("Download-Btn", "btn-dropdown")}>
          <Icon icon="download" />
          <span className="Download-Text"><Trans>Download</Trans></span>
        </Dropdown.Button>
        <Dropdown.ListBox className="Download-Options">
          <Dropdown.Option className="Download-Option" value="download_media">
            <Trans>Download the video</Trans>
          </Dropdown.Option>
          <Dropdown.Option className="Download-Option" value="download_with_captions">
            <Trans>Download the video and captions file(s)</Trans>
          </Dropdown.Option>
        </Dropdown.ListBox>
      </Dropdown>
    </div>
  );
};

export default DownloadDropdown;
